import ApiClient from '../../api/client';

import fetcher from './fetcher';

import { objectSelector as _get } from '../../utils';

import { getValue } from '../connectSelector';
import {
  setReady,
  setPending,
  setSameBillAddress,
  setFundsTransferAccountId,
  setCreditPaymentOptionId,
  setPayOnDeliveryMethodName,
  setSelectedSampleProducts,
  setSampleProductsSaved,
  setGiftNote,
  setRewards,
} from './reducer';

import {
  selectSameBillAddress,
  selectCurrentBillingId,
  selectCurrentAddressId,
  selectPaymentOptions,
  selectAgreement,
  selectCardNumber,
  selectCardMonth,
  selectCardYear,
  selectCardCVV,
  selectCardHolder,
  selectUseThreeD,
} from './selectors';

export const fetchCheckout = () => async dispatch => {
  await fetcher(ApiClient.checkout.fetchCheckout());

  const addressId = getValue(selectCurrentAddressId);
  const billingId = getValue(selectCurrentBillingId);
  const isSameAddress = addressId === billingId;

  if (addressId && billingId && !isSameAddress) {
    dispatch(setSameBillAddress(isSameAddress));
  }
  dispatch(setReady());
};

export const fetchAddresses = () => () => {
  fetcher(ApiClient.checkout.fetchAddresses());
};

export const setDeliveryOption = (pk) => () => {
  fetcher(ApiClient.checkout.setDeliveryOption(pk));
};

export const setAddress = (pk) => () => {
  const sameAddress = getValue(selectSameBillAddress);
  let billAddressPk = sameAddress ? pk : getValue(selectCurrentBillingId);

  fetcher(ApiClient.checkout.setAddress(pk, billAddressPk));
};

export const setBillingAddress = (pk) => () => {
  const shippingAddress = getValue(selectCurrentAddressId);

  fetcher(ApiClient.checkout.setAddress(shippingAddress, pk));
};

export const setShippingOption = (pk) => () => {
  fetcher(ApiClient.checkout.setShippingOption(pk));
};

export const setPaymentOption = (pk) => () => {
  fetcher(ApiClient.checkout.setPaymentOption(pk));
};

export const setBinNumber = (bin) => () => {
  fetcher(ApiClient.checkout.setBinNumber(bin));
};

export const setInstallment = (pk) => () => {
  fetcher(ApiClient.checkout.setInstallment(pk));
};

export const setSameBillingAddress = (isSame) => (dispatch) => {
  dispatch(setSameBillAddress(isSame));

  if (isSame) {
    const billingId = getValue(selectCurrentBillingId);
    const shippingId = getValue(selectCurrentAddressId);

    if (billingId !== shippingId) {
      dispatch(setAddress(shippingId));
    }
  }
};

export const completeCheckout = () => async (dispatch) => {
  const data = {
    agreement: getValue(selectAgreement),
    card_holder: getValue(selectCardHolder),
    card_number: getValue(selectCardNumber),
    card_month: getValue(selectCardMonth),
    card_year: getValue(selectCardYear),
    card_cvv: getValue(selectCardCVV),
    use_three_d: getValue(selectUseThreeD),
  };

  dispatch(setPending(true));

  await fetcher(ApiClient.checkout.completeCheckout(data));

  dispatch(setPending(false));
};

export const setFundsTransferAccount = (pk) => (dispatch) => {
  dispatch(setFundsTransferAccountId(pk));

  fetcher(ApiClient.checkout.setFundsTransferAccount(pk));
};

export const setWalletHepsiPaySelectionPage = () => () => {
  const paymentOptions = getValue(selectPaymentOptions);
  const walletPaymentOption = paymentOptions.find((opt) => opt.slug === 'hepsipay')?.pk;
  
  fetcher(ApiClient.checkout.setWalletSelectionPage(walletPaymentOption));
}

export const setWalletPaymentPage = () => () => {
  fetcher(ApiClient.checkout.setWalletPaymentPage());
}

export const completeWalletPage = (success) => () => {
  fetcher(ApiClient.checkout.completeWalletPage(success));
}

export const completeFundsTransfer = () => async (dispatch) => {
  const data = {
    agreement: getValue(selectAgreement),
  };

  dispatch(setPending(true));

  await fetcher(ApiClient.checkout.completeFundsTransfer(data));

  dispatch(setPending(false));
};

export const setCreditPaymentOption = (pk) => (dispatch) => {
  dispatch(setCreditPaymentOptionId(pk));

  fetcher(ApiClient.checkout.setCreditPaymentOption(pk));
};

export const creditPaymentConfirmation = () => async (dispatch) => {
  const data = {
    agreement: getValue(selectAgreement),
  };

  dispatch(setPending(true));

  await fetcher(ApiClient.checkout.creditPaymentConfirmation(data));

  dispatch(setPending(false));
};

export const setPayOnDeliveryMethod = (paymentChoice) => (dispatch) => {
  dispatch(setPayOnDeliveryMethodName(paymentChoice));

  fetcher(ApiClient.checkout.setPayOnDeliveryMethod(paymentChoice));
};

export const completePayOnDeliveryMethod = () => async (dispatch) => {
  const data = {
    agreement: getValue(selectAgreement),
  };

  dispatch(setPending(true));

  await fetcher(ApiClient.checkout.completePayOnDelivery(data));

  dispatch(setPending(false));
};

export const saveSampleProducts = (products) => async (dispatch) => {
  await fetcher(ApiClient.checkout.saveSampleProducts(products));
  dispatch(setSelectedSampleProducts(products));
  dispatch(setSampleProductsSaved(true));
};

export const fetchLoyaltyData = () => async () => {
  await fetcher(ApiClient.checkout.fetchLoyaltyData());
};

export const setLoyaltyData = (amount) => async (dispatch) => {
  dispatch(setPending(true))

  await fetcher(ApiClient.checkout.setLoyaltyData(amount));
  await fetcher(ApiClient.checkout.fetchLoyaltyData());
  
  dispatch(setPending(false))
};

export const setRetailStore = (retailStore, billingAddressId) => () => {
  fetcher(ApiClient.checkout.setRetailStore(retailStore, billingAddressId));
};

export const setGiftBox = (note) => (dispatch) => {
  dispatch(setGiftNote(note));

  fetcher(ApiClient.checkout.setGiftBox(note));
};

export const deleteGiftBox = () => (dispatch) => {
  dispatch(setGiftNote(null));

  fetcher(ApiClient.checkout.deleteGiftBox());
};

export const setReservation = ({ skipReservation = true, upper, lower }) => async () => {
  await fetcher(ApiClient.checkout.setReservation(skipReservation, upper, lower));
};

export const setMasterPassBinNumber = (binNumber, isStoredCard = false) => () => {
  fetcher(ApiClient.checkout.setMasterPassBinNumber(binNumber, isStoredCard));
};

export const setMasterPassInstallment = (pk) => async (dispatch) => {
  dispatch(setPending(true))

  await fetcher(ApiClient.checkout.setMasterPassInstallment(pk));
  
  dispatch(setPending(false))
};

export const getMasterPassOrderNo = () => () => {
  fetcher(ApiClient.checkout.getMasterPassOrderNo());
};

export const completeMasterPassOrder = (token) => () => {
  fetcher(ApiClient.checkout.completeMasterPassOrder(token));
};

export const setHasRewards = rewards => dispatch => {
  dispatch(setRewards(rewards));
};
